import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, User, Globe, ThumbsUp, ThumbsDown, Send, ArrowLeft, Bot, Search, Filter, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './ConversationHistory.css';
import './ViewConversationsMockup.css';

const ConversationHistory = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, direct, embedded
  const [ownerResponse, setOwnerResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({}); // messageId -> 'up' | 'down'
  
  // Advanced filtering states
  const [readStatusFilter, setReadStatusFilter] = useState('all'); // all, read, unread
  const [recencyFilter, setRecencyFilter] = useState('all'); // all, 24h, 7d, 30d
  const [userFilter, setUserFilter] = useState('all'); // all, specific user
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chat'
  const [reviewedConversations, setReviewedConversations] = useState(new Set());
  
  // Search and filter UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Load reviewed conversations from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('reviewedConversations');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setReviewedConversations(new Set(parsed));
      } catch (error) {
        console.error('Error loading reviewed conversations:', error);
      }
    }
  }, []);

  // Save reviewed conversations to localStorage when it changes
  useEffect(() => {
    if (reviewedConversations.size > 0) {
      localStorage.setItem('reviewedConversations', JSON.stringify([...reviewedConversations]));
    }
  }, [reviewedConversations]);

  const fetchConversations = useCallback(async () => {
    if (!user?.agent?.id) return;
    
    try {
      const response = await fetch(`/api/agents/${user.agent.id}/conversations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched conversations for agent', user.agent.id, ':', data.conversations?.length || 0);
        console.log('Raw conversations data:', data.conversations);
        
        // Debug: Check for conversation 1031 specifically
        const conv1031 = data.conversations?.find(conv => conv.id === 1031);
        console.log('Conversation 1031 found:', conv1031);
        
        // Debug: Check all embedded conversations
        const embeddedConvs = data.conversations?.filter(conv => conv.is_embedded === true);
        console.log('Embedded conversations found:', embeddedConvs?.length || 0);
        console.log('Embedded conversation IDs:', embeddedConvs?.map(c => c.id) || []);
        
        // Load user data for each conversation (but not messages - they're already included)
        const conversationsWithUserData = await Promise.all(
          (data.conversations || []).map(async (conversation) => {
            // Load user data if user_id exists
            let userData = null;
            if (conversation.user_id) {
              try {
                const userResponse = await fetch(`/api/agents/users/${conversation.user_id}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                });
                
                if (userResponse.ok) {
                  userData = await userResponse.json();
                }
              } catch (error) {
                console.error('Error fetching user data for user', conversation.user_id, ':', error);
              }
            }
            
            return {
              ...conversation,
              user_data: userData
            };
          })
        );
        
        setConversations(conversationsWithUserData);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.agent?.id]);

  useEffect(() => {
    if (user?.agent?.id) {
      fetchConversations();
    }
  }, [user?.agent?.id, fetchConversations]);

  const fetchConversationDetails = async (conversationId) => {
    if (!user?.agent?.id) return;
    
    try {
      const response = await fetch(`/api/agents/${user.agent.id}/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
        setViewMode('chat');
        
        // Mark conversation as reviewed
        setReviewedConversations(prev => new Set([...prev, conversationId]));
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  const handleBackToTable = () => {
    setViewMode('table');
    setSelectedConversation(null);
  };

  const getFirstMessagePreview = (conversation) => {
    // Use the first_user_message field from the API (optimized)
    if (conversation.first_user_message) {
      return conversation.first_user_message.length > 80 
        ? conversation.first_user_message.substring(0, 80) + '...'
        : conversation.first_user_message;
    }
    
    // Fallback to messages array if first_user_message is not available
    if (conversation.messages && conversation.messages.length > 0) {
      const firstUserMessage = conversation.messages.find(msg => msg.role === 'user');
      if (firstUserMessage) {
        return firstUserMessage.content.length > 80 
          ? firstUserMessage.content.substring(0, 80) + '...'
          : firstUserMessage.content;
      }
    }
    
    return 'No messages yet';
  };

  const getUserDisplay = (conversation) => {
    // For embedded conversations, show "Embedded"
    if (conversation.is_embedded) {
      return {
        name: 'Embedded',
        avatar: null
      };
    }
    
    // For direct conversations, check if we have user data from the users table
    if (conversation.user_data) {
      return {
        name: conversation.user_data.name || 'Unknown User',
        avatar: conversation.user_data.picture || null
      };
    }
    
    // Fallback to conversation-level user info if available
    if (conversation.user_name) {
      return {
        name: conversation.user_name,
        avatar: conversation.user_picture
      };
    }
    
    // Final fallback to Anonymous User
    return {
      name: 'Anonymous User',
      avatar: null
    };
  };

  const getNewMessageIndicator = (conversation) => {
    // Show notification dot if conversation has messages and hasn't been reviewed
    const hasMessages = (conversation.message_count || 0) > 0;
    const isReviewed = reviewedConversations.has(conversation.id);
    return hasMessages && !isReviewed;
  };

  const getOwnerResponseStatus = (conversation) => {
    const state = conversation.owner_response_state || 'not_requested';
    const engagementFee = conversation.engagement_fee_cents || 0;
    
    switch (state) {
      case 'requested':
        return (
          <div className="owner-response-requested">
            <span className="status-badge requested">Requested</span>
            {conversation.requested_at && (
              <div className="timestamp">
                {formatDate(conversation.requested_at)}
              </div>
            )}
            {engagementFee > 0 && (
              <div className="fee">
                ${(engagementFee / 100).toFixed(2)}
              </div>
            )}
          </div>
        );
      case 'fulfilled':
        return (
          <div className="owner-response-fulfilled">
            <span className="status-badge fulfilled">Completed</span>
            {conversation.fulfilled_at && (
              <div className="timestamp">
                {formatDate(conversation.fulfilled_at)}
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="owner-response-not-requested">
            <span className="status-badge not-requested">Not Requested</span>
          </div>
        );
    }
  };

  const sendOwnerResponse = async () => {
    if (!ownerResponse.trim() || !selectedConversation || sendingResponse) return;
    
    setSendingResponse(true);
    try {
      const response = await fetch(`/api/agents/${user.agent.id}/conversations/${selectedConversation.id}/owner-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: ownerResponse.trim() })
      });
      
      if (response.ok) {
        const result = await response.json();
        setOwnerResponse('');
        
        // If owner engagement was fulfilled, update the conversation state
        if (result.owner_engagement_fulfilled) {
          console.log('✅ [CONVERSATION_HISTORY] Owner engagement fulfilled');
          // Update the conversation in the list
          setConversations(prev => prev.map(conv => 
            conv.id === selectedConversation.id 
              ? { ...conv, owner_response_state: 'fulfilled', fulfilled_at: new Date().toISOString() }
              : conv
          ));
        }
        
        // Refresh the conversation to show the new response
        await fetchConversationDetails(selectedConversation.id);
      }
    } catch (error) {
      console.error('Error sending owner response:', error);
    } finally {
      setSendingResponse(false);
    }
  };

  const sendMessageFeedback = async (messageId, feedback) => {
    try {
      const response = await fetch(`/api/agents/${user.agent.id}/messages/${messageId}/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ feedback })
      });
      
      if (response.ok) {
        setMessageFeedback(prev => ({
          ...prev,
          [messageId]: feedback
        }));
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  console.log('Starting filter with:', {
    totalConversations: conversations.length,
    currentFilter: filter,
    searchQuery: searchQuery,
    readStatusFilter: readStatusFilter,
    recencyFilter: recencyFilter,
    userFilter: userFilter
  });
  
  const filteredConversations = conversations.filter(conv => {
    // Show external conversations only (embedded or direct from external users)
    // External conversations are: is_embedded = true OR (is_embedded = false AND user_id is NOT NULL)
    // Owner conversations are: is_embedded = false AND user_id is NULL (owner's own conversations)
    
    const isExternalConversation = conv.is_embedded || (!conv.is_embedded && conv.user_id);
    
    // Debug logging for conversation 1031
    if (conv.id === 1031) {
      console.log('Filtering conversation 1031:', {
        id: conv.id,
        is_embedded: conv.is_embedded,
        user_id: conv.user_id,
        isExternalConversation: isExternalConversation,
        title: conv.title
      });
    }
    
    // Only show external conversations
    if (!isExternalConversation) {
      if (conv.id === 1031) {
        console.log('Conversation 1031 filtered out at isExternalConversation check');
      }
      return false;
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const firstMessage = getFirstMessagePreview(conv).toLowerCase();
      const userDisplay = getUserDisplay(conv);
      const userName = userDisplay.name.toLowerCase();
      
      if (!firstMessage.includes(query) && !userName.includes(query)) {
        return false;
      }
    }
    
    // Filter by conversation type (all, direct, embedded)
    if (filter === 'all') {
      // Continue to other filters
    } else if (filter === 'direct') {
      if (conv.is_embedded) return false;
    } else if (filter === 'embedded') {
      if (!conv.is_embedded) return false;
    }
    
    // Filter by read status
    if (readStatusFilter === 'read') {
      if (!reviewedConversations.has(conv.id)) return false;
    } else if (readStatusFilter === 'unread') {
      if (reviewedConversations.has(conv.id)) return false;
    }
    
    // Filter by recency
    if (recencyFilter !== 'all') {
      const now = new Date();
      const convDate = new Date(conv.created_at);
      const hoursDiff = (now - convDate) / (1000 * 60 * 60);
      
      if (recencyFilter === '24h' && hoursDiff > 24) return false;
      if (recencyFilter === '7d' && hoursDiff > 24 * 7) return false;
      if (recencyFilter === '30d' && hoursDiff > 24 * 30) return false;
    }
    
    // Filter by user
    if (userFilter !== 'all') {
      const userDisplay = getUserDisplay(conv);
      if (userDisplay.name !== userFilter) return false;
    }
    
    return true;
  });
  
  // Debug: Check if conversation 1031 made it through filtering
  const conv1031AfterFilter = filteredConversations.find(conv => conv.id === 1031);
  console.log('Conversation 1031 after filtering:', conv1031AfterFilter ? 'FOUND' : 'NOT FOUND');
  console.log('Total conversations after filtering:', filteredConversations.length);
  
  console.log('Filtered conversations count:', filteredConversations.length);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getConversationType = (conversation) => {
    if (conversation.is_embedded) {
      return { type: 'Embedded', icon: Globe, color: 'blue' };
    }
    return { type: 'Direct', icon: User, color: 'green' };
  };

  // Get unique users for filter dropdown
  const getUniqueUsers = () => {
    const users = new Set();
    conversations.forEach(conv => {
      const userDisplay = getUserDisplay(conv);
      users.add(userDisplay.name);
    });
    return Array.from(users).sort();
  };

  if (loading) {
    return (
      <div className="conversation-history">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  // Chat Detail View
  if (viewMode === 'chat' && selectedConversation) {
    return (
      <div className="view-conversations-mockup" data-theme="dark">
        {/* Header */}
        <div className="view-conversations-header">
          <button className="view-conversations-back-btn" onClick={handleBackToTable}>
            <ArrowLeft size={16} />
            Back to Conversations
          </button>
          <div className="view-conversations-info">
            <h2>Conversation with {getUserDisplay(selectedConversation).name}</h2>
          </div>
        </div>

        {/* Messages List */}
        <div className="view-conversations-messages">
          {selectedConversation.messages?.map((message, index) => (
            <div key={index} className={`view-conversations-message ${message.role === 'user' ? 'user' : 'bot'}`}>
              {message.role === 'user' && (
                <div className="view-conversations-avatar">
                  <div className="user-avatar-mockup">
                    {getUserDisplay(selectedConversation).name.charAt(0).toUpperCase()}
                  </div>
                </div>
              )}
              <div className="view-conversations-message-content">
                <div className="view-conversations-message-text">
                  <div className="view-conversations-message-header">
                    <span className="message-sender">
                      {message.role === 'user' ? getUserDisplay(selectedConversation).name : 
                       message.is_owner_response ? 'Owner' : 'Agent'}
                    </span>
                    {message.role === 'assistant' && !message.is_owner_response && (
                      <div className="view-conversations-feedback-icons">
                        <ThumbsUp 
                          className={`view-conversations-feedback-icon thumbs-up ${messageFeedback[message.id] === 'up' ? 'selected' : ''}`}
                          onClick={() => sendMessageFeedback(message.id, 'up')}
                          size={18}
                        />
                        <ThumbsDown 
                          className={`view-conversations-feedback-icon thumbs-down ${messageFeedback[message.id] === 'down' ? 'selected' : ''}`}
                          onClick={() => sendMessageFeedback(message.id, 'down')}
                          size={18}
                        />
                      </div>
                    )}
                  </div>
                  {message.content}
                </div>
              </div>
              {message.role === 'assistant' && !message.is_owner_response && (
                <div className="view-conversations-avatar">
                  <div className="view-conversations-bot-avatar">
                    <Bot size={20} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Owner Jump-in Input */}
        <div className="input-area conversation-history-input">
          <div className="input-container">
            <input
              type="text"
              value={ownerResponse}
              onChange={(e) => setOwnerResponse(e.target.value)}
              placeholder="Respond as owner..."
              className="message-input"
              onKeyPress={(e) => e.key === 'Enter' && sendOwnerResponse()}
              disabled={sendingResponse}
            />
            <button onClick={sendOwnerResponse} className="send-button" disabled={!ownerResponse.trim() || sendingResponse}>
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Table View
  return (
    <div className="conversation-history">
      <div className="history-header">
        <h2>Conversations</h2>
      </div>


      <div className="conversations-table-container">
        {/* Search and Filter Bar */}
        <div className="table-controls">
          <div className="search-container">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="table-actions">
            <span className="results-count">{filteredConversations.length} conversations</span>
            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filters
            </button>
            <button className="refresh-btn" onClick={fetchConversations}>
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="filter-panel">
            <div className="filter-grid">
              <div className="filter-group">
                <label>Type</label>
                <select 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Conversations</option>
                  <option value="direct">Direct Chats</option>
                  <option value="embedded">Embedded Chats</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Read Status</label>
                <select 
                  value={readStatusFilter} 
                  onChange={(e) => setReadStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Recency</label>
                <select 
                  value={recencyFilter} 
                  onChange={(e) => setRecencyFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>User</label>
                <select 
                  value={userFilter} 
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Users</option>
                  {getUniqueUsers().map(user => (
                    <option key={user} value={user}>{user}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="filter-actions">
              <button 
                className="clear-filters-btn"
                onClick={() => {
                  setFilter('all');
                  setReadStatusFilter('all');
                  setRecencyFilter('all');
                  setUserFilter('all');
                  setSearchQuery('');
                }}
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {filteredConversations.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} className="empty-icon" />
            {searchQuery.trim() || filter !== 'all' || readStatusFilter !== 'all' || recencyFilter !== 'all' || userFilter !== 'all' ? (
              <>
                <h3>No results found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </>
            ) : (
              <>
                <h3>No conversations yet</h3>
                <p>Conversations will appear here once people start chatting with your agent</p>
              </>
            )}
          </div>
        ) : (
          <div className="conversations-table-container">
            <table className="conversations-table">
              <thead>
                <tr>
                  <th className="status-column"></th>
                  <th>Timestamp</th>
                  <th>First Message</th>
                  <th>User</th>
                  <th>Messages</th>
                  <th>Owner Response</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredConversations.map((conversation) => {
                  const userDisplay = getUserDisplay(conversation);
                  const hasNew = getNewMessageIndicator(conversation);
                  const conversationType = getConversationType(conversation);
                  
                  
                  return (
                    <tr 
                      key={conversation.id}
                      className="conversation-row"
                      onClick={() => fetchConversationDetails(conversation.id)}
                    >
                      <td className="status-column">
                        <div className="status-indicator">
                          {hasNew && (
                            <div className={`new-message-dot ${conversationType.color}`}></div>
                          )}
                        </div>
                      </td>
                      <td className="timestamp-column">
                        {formatDate(conversation.created_at)}
                      </td>
                      <td className="message-preview-column">
                        <div className="message-preview">
                          {getFirstMessagePreview(conversation)}
                        </div>
                      </td>
                      <td className="user-column">
                        <div className="user-info">
                          {userDisplay.avatar ? (
                            <img 
                              src={userDisplay.avatar} 
                              alt={userDisplay.name}
                              className="user-avatar"
                            />
                          ) : (
                            <div className="user-avatar anonymous">
                              <User size={16} />
                            </div>
                          )}
                          <span className="user-name">{userDisplay.name}</span>
                        </div>
                      </td>
                      <td className="messages-count-column">
                        <span className="messages-count">{conversation.message_count || 0}</span>
                      </td>
                      <td className="owner-response-column">
                        <div className="owner-response-status">
                          {getOwnerResponseStatus(conversation)}
                        </div>
                      </td>
                      <td className="actions-column">
                        <button 
                          className="view-conversation-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchConversationDetails(conversation.id);
                          }}
                        >
                          View Conversation
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationHistory;
